import React, { useState } from "react";
import cn from "classnames";
import { Button, Col, Row } from "reactstrap";
import Icofont from "react-icofont";
import { useIntl } from "react-intl";
import { FormInputCategoryOption } from "../../../../../cores/webForm/interfaces/formInputOptions";
import { InputWembii } from "../../../InputWembii";

interface Props {
  categories: FormInputCategoryOption[];
  onChange: (categories: FormInputCategoryOption[]) => void;
}

export default function CategoryOptionsEditor({
  categories,
  onChange,
}: Props) {
  const intl = useIntl();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    categories.length ? 0 : null
  );

  const updateCategory = (
    index: number,
    patch: Partial<FormInputCategoryOption>
  ) => {
    onChange(
      categories.map((category, categoryIndex) =>
        categoryIndex === index ? { ...category, ...patch } : category
      )
    );
  };

  const addCategory = () => {
    const nextIndex = categories.length;
    onChange([
      ...categories,
      {
        label: `${intl.formatMessage({ id: "app.editForm.category" })} ${
          nextIndex + 1
        }`,
        subcategories: [
          `${intl.formatMessage({ id: "app.editForm.subcategory" })} 1`,
        ],
      },
    ]);
    setExpandedIndex(nextIndex);
  };

  const removeCategory = (index: number) => {
    const next = categories.filter((_, categoryIndex) => categoryIndex !== index);
    onChange(next);
    if (expandedIndex === index) {
      setExpandedIndex(next.length ? 0 : null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const addSubcategory = (index: number) => {
    const category = categories[index];
    if (!category) return;
    updateCategory(index, {
      subcategories: [
        ...category.subcategories,
        `${intl.formatMessage({ id: "app.editForm.subcategory" })} ${
          category.subcategories.length + 1
        }`,
      ],
    });
  };

  const updateSubcategory = (
    categoryIndex: number,
    subcategoryIndex: number,
    value: string
  ) => {
    const category = categories[categoryIndex];
    if (!category) return;
    updateCategory(categoryIndex, {
      subcategories: category.subcategories.map((subcategory, index) =>
        index === subcategoryIndex ? value : subcategory
      ),
    });
  };

  const removeSubcategory = (
    categoryIndex: number,
    subcategoryIndex: number
  ) => {
    const category = categories[categoryIndex];
    if (!category) return;
    updateCategory(categoryIndex, {
      subcategories: category.subcategories.filter(
        (_, index) => index !== subcategoryIndex
      ),
    });
  };

  return (
    <Col xs="12" className="mb-3">
      <div className="web-form-category-editor">
        {categories.map((category, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <div key={`category-${index}`} className="web-form-category-editor__item">
              <button
                type="button"
                className={cn("web-form-category-editor__header", {
                  "web-form-category-editor__header--expanded": isExpanded,
                })}
                onClick={() => setExpandedIndex(isExpanded ? null : index)}
              >
                <span className="web-form-category-editor__title">
                  {category.label ||
                    `${intl.formatMessage({ id: "app.editForm.category" })} ${
                      index + 1
                    }`}
                </span>
                <Icofont
                  icon={isExpanded ? "rounded-up" : "rounded-down"}
                  className="web-form-category-editor__chevron"
                />
              </button>

              {isExpanded && (
                <div className="web-form-category-editor__body">
                  <Row className="align-items-center mb-2">
                    <Col xs="10">
                      <InputWembii
                        type="text"
                        name={`category-label-${index}`}
                        id={`category-label-${index}`}
                        label={intl.formatMessage({
                          id: "app.editForm.categoryName",
                        })}
                        value={category.label}
                        flat
                        onChange={(value) =>
                          updateCategory(index, { label: value })
                        }
                      />
                    </Col>
                    <Col xs="2" className="text-right">
                      <div
                        onClick={() => removeCategory(index)}
                        className="text-danger pointer d-inline-block mt-4"
                      >
                        <Icofont icon="ui-delete" />
                      </div>
                    </Col>
                  </Row>

                  <p className="web-form-category-editor__subtitle mb-2">
                    {intl.formatMessage({ id: "app.editForm.subcategories" })}
                  </p>

                  {category.subcategories.map((subcategory, subcategoryIndex) => (
                    <Row
                      key={`subcategory-${index}-${subcategoryIndex}`}
                      className="align-items-center mb-2"
                    >
                      <Col xs="10">
                        <InputWembii
                          type="text"
                          name={`subcategory-${index}-${subcategoryIndex}`}
                          id={`subcategory-${index}-${subcategoryIndex}`}
                          placeholder={intl.formatMessage({
                            id: "app.editForm.subcategory",
                          })}
                          value={subcategory}
                          flat
                          onChange={(value) =>
                            updateSubcategory(index, subcategoryIndex, value)
                          }
                        />
                      </Col>
                      <Col xs="2" className="text-right">
                        <div
                          onClick={() =>
                            removeSubcategory(index, subcategoryIndex)
                          }
                          className="text-danger pointer"
                        >
                          <Icofont icon="ui-delete" />
                        </div>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    type="button"
                    size="sm"
                    color="secondary"
                    outline
                    onClick={() => addSubcategory(index)}
                  >
                    <Icofont icon="plus" />{" "}
                    {intl.formatMessage({ id: "app.editForm.addSubcategory" })}
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        <Button
          type="button"
          size="sm"
          color="primary"
          outline
          className="mt-2"
          onClick={addCategory}
        >
          <Icofont icon="plus" />{" "}
          {intl.formatMessage({ id: "app.editForm.addCategory" })}
        </Button>
      </div>
    </Col>
  );
}
